export type OnboardingStepStatus = "completed" | "active" | "upcoming";

export const ONBOARDING_STEPS = [
  { id: "welcome", path: "/onboard" },
  { id: "company", path: "/onboard/company" },
  { id: "contact", path: "/onboard/contact" },
  { id: "about", path: "/onboard/about" },
  { id: "documents", path: "/onboard/documents" },
  { id: "services", path: "/onboard/services" },
  { id: "review", path: "/onboard/review" },
] as const;

export type OnboardingStepId = (typeof ONBOARDING_STEPS)[number]["id"];

export function getStepIndex(id: OnboardingStepId): number {
  return ONBOARDING_STEPS.findIndex((step) => step.id === id);
}

export function getOnboardingStepStatus(
  stepIndex: number,
  activeStepIndex: number,
): OnboardingStepStatus {
  if (stepIndex < activeStepIndex) return "completed";
  if (stepIndex === activeStepIndex) return "active";
  return "upcoming";
}

export interface OnboardingDraft {
  name: string;
  legalName: string;
  crNumber: string;
  vatNumber: string;
  unifiedNumber: string;
  buildingNumber: string;
  street: string;
  district: string;
  city: string;
  postalCode: string;
  additionalNumber: string;
  shortAddress: string;
  logoFileId: string;
  contactName: string;
  companyEmail: string;
  contactPhone: string;
  websiteUrl: string;
  description: string;
}

export const EMPTY_ONBOARDING_DRAFT: OnboardingDraft = {
  name: "",
  legalName: "",
  crNumber: "",
  vatNumber: "",
  unifiedNumber: "",
  buildingNumber: "",
  street: "",
  district: "",
  city: "",
  postalCode: "",
  additionalNumber: "",
  shortAddress: "",
  logoFileId: "",
  contactName: "",
  companyEmail: "",
  contactPhone: "",
  websiteUrl: "",
  description: "",
};

export function draftFromProfile(profile: {
  name?: string | null;
  legalName?: string | null;
  crNumber?: string | null;
  vatNumber?: string | null;
  unifiedNumber?: string | null;
  buildingNumber?: string | null;
  street?: string | null;
  district?: string | null;
  city?: string | null;
  postalCode?: string | null;
  additionalNumber?: string | null;
  shortAddress?: string | null;
  logoFileId?: string | null;
  contactName?: string | null;
  companyEmail?: string | null;
  contactPhone?: string | null;
  websiteUrl?: string | null;
  description?: string | null;
}): OnboardingDraft {
  return {
    name: profile.name ?? "",
    legalName: profile.legalName ?? "",
    crNumber: profile.crNumber ?? "",
    vatNumber: profile.vatNumber ?? "",
    unifiedNumber: profile.unifiedNumber ?? "",
    buildingNumber: profile.buildingNumber ?? "",
    street: profile.street ?? "",
    district: profile.district ?? "",
    city: profile.city ?? "",
    postalCode: profile.postalCode ?? "",
    additionalNumber: profile.additionalNumber ?? "",
    shortAddress: profile.shortAddress ?? "",
    logoFileId: profile.logoFileId ?? "",
    contactName: profile.contactName ?? "",
    companyEmail: profile.companyEmail ?? "",
    contactPhone: profile.contactPhone ?? "",
    websiteUrl: profile.websiteUrl ?? "",
    description: profile.description ?? "",
  };
}

/** Build PATCH body; omit empty optional strings so we don't clear server values unintentionally. */
export function draftToPayload(draft: OnboardingDraft) {
  return Object.fromEntries(
    (Object.entries(draft) as [keyof OnboardingDraft, string][])
      .map(([key, value]) => {
        const trimmed = value.trim();
        if (trimmed.length === 0) return null;
        return [key, key === "shortAddress" ? trimmed.toUpperCase() : trimmed] as const;
      })
      .filter((entry): entry is readonly [keyof OnboardingDraft, string] => entry !== null),
  );
}
